import moment from 'moment'
import { useMemo } from 'react'
import { useQuery } from 'urql'
import { CONTACT_PROCEDURES_QUERY, CONTACT_TREATMENT_PLANS_QUERY } from './graphql'
import { buildPatientIdSearch } from 'app/hooks/useContactRelatedObjects'
import type { AppointmentJson } from 'app/types/entities'
import type { RawAppointment, RawPlan, RawProcedure, TreatmentProcedure } from 'lib/treatmentCards'
import { isDeadPlan, isDeleted, planForProcedure, statusOf } from 'lib/treatmentCards'
import { planIdsFor } from 'lib/treatmentPlanLines'

/**
 * Well past any real patient's outstanding work, and small enough that a
 * runaway query cannot quietly page through the practice.
 */
const PROCEDURE_LIMIT = 500

/**
 * A contact's outstanding treatment, classified.
 *
 * Only `PENDING` procedures are asked for. Completed work is every cleaning the
 * patient ever had, which on a long-standing patient runs to over a hundred rows
 * and buries the thing someone opened this tab to see. What they have spent
 * before is one number, `productionLtv`, not a list.
 *
 * The appointments come from the drawer's own load rather than a third query.
 * They are what decides whether pending work is booked, and the drawer already
 * has all of them.
 */
export function useTreatmentWork({
  patientIds,
  appointments,
}: {
  patientIds: string[]
  appointments?: AppointmentJson[]
}) {
  const search = buildPatientIdSearch(patientIds)

  const [{ data: procedureData, fetching: fetchingProcedures, error: procedureError }] = useQuery<{
    procedures?: { hits?: RawProcedure[]; total?: number }
  }>({
    query: CONTACT_PROCEDURES_QUERY,
    variables: { search: `${search} AND state:"PENDING"`, pagination: { limit: PROCEDURE_LIMIT } },
    pause: !search,
  })

  const [{ data: planData, fetching: fetchingPlans, error: planError }] = useQuery<{
    treatmentPlans?: { hits?: RawPlan[] }
  }>({
    query: CONTACT_TREATMENT_PLANS_QUERY,
    variables: { search, pagination: { limit: PROCEDURE_LIMIT } },
    pause: !search,
  })

  const procedures = useMemo(() => {
    const raw = procedureData?.procedures?.hits ?? []
    const plans = planData?.treatmentPlans?.hits ?? []
    const plansById = new Map(plans.map((plan) => [plan.id, plan]))
    const appointmentsById = new Map(
      (appointments ?? [])
        .filter((appointment) => !isDeleted(appointment))
        .map((appointment) => [appointment.id as string, appointment as RawAppointment]),
    )
    const now = moment()

    return (
      raw
        // Priced work only: a zero-fee line is bookkeeping, not treatment anyone
        // is waiting on.
        .filter((item) => (item.feeAmount ?? 0) > 0)
        .map((item): TreatmentProcedure => {
          // A procedure no plan carries, mostly routine hygiene, still shows,
          // in its own "Not on a plan" group below the plan work.
          const planIds = planIdsFor(item, plans)
          const plan = planForProcedure(planIds, plansById)
          const { status, on } = statusOf(item, appointmentsById, now)
          return {
            code: item.procedureCode ?? ``,
            description: (item.description ?? ``).trim(),
            tooth: item.tooth ?? ``,
            fee: Math.round(item.feeAmount ?? 0),
            status,
            on,
            plan: plan?.displayName ?? null,
            planned: (item.procedureCreateDate ?? ``).slice(0, 10),
            dead: isDeadPlan(plan),
            offPlan: planIds.length === 0,
            planIds,
            priority: item.priority ?? null,
          }
        })
        .sort((a, b) => b.fee - a.fee)
    )
  }, [procedureData, planData, appointments])

  return {
    procedures,
    plans: planData?.treatmentPlans?.hits ?? [],
    fetching: fetchingProcedures || fetchingPlans,
    error: procedureError ?? planError,
    hasPatientLink: !!search,
  }
}
